import { encodeParams } from "./url";

interface BaseOptions {
  cleanParameters?: boolean;
  parameters?: Record<string, string | number | boolean>;
  url: string;
  useApiKeys?: boolean;
  withCredentials?: boolean;
}

interface GetOptions extends BaseOptions {
  method?: "GET";
}

interface PostOptions extends BaseOptions {
  body?: Record<string, string | number | boolean | undefined>;
  method?: "POST";
}

interface DeleteOptions extends BaseOptions {
  method?: "DELETE";
}

interface PutOptions extends BaseOptions {
  body?: Record<string, string | number | boolean | undefined>;
  method?: "PUT";
}

interface RequestOptions extends BaseOptions {
  body?: Record<string, string | number | boolean>;
  method: "GET" | "POST" | "DELETE" | "PUT";
}
function typedPut<T>(options: PutOptions): Promise<T> {
  return request.apply(this, [options]);
}

export function put<T>({
  body,
  cleanParameters,
  parameters,
  url,
  withCredentials = false
}: PutOptions): Promise<T> {
  return typedPut<T>({
    body,
    cleanParameters,
    method: "PUT",
    parameters,
    url,
    withCredentials
  });
}

function typedGet<T>(options: GetOptions): Promise<T> {
  return request.apply(this, [options]);
}

export function getJson<T>({
  cleanParameters,
  parameters,
  url,
  withCredentials = false
}: GetOptions): Promise<T> {
  return typedGet<T>({
    cleanParameters,
    method: "GET",
    parameters,
    url,
    withCredentials
  });
}

function typedPost<T>(options: PostOptions): Promise<T> {
  return request.apply(this, [options]);
}

export function post<T>({
  body,
  cleanParameters,
  parameters,
  url,
  withCredentials = false
}: PostOptions): Promise<T> {
  return typedPost<T>({
    body,
    cleanParameters,
    method: "POST",
    parameters,
    url,
    withCredentials
  });
}

function typedDelete<T>(options: DeleteOptions): Promise<T> {
  return request.apply(this, [options]);
}

export function deleteJson<T>({
  cleanParameters,
  parameters,
  url,
  withCredentials = false
}: DeleteOptions): Promise<T> {
  return typedDelete<T>({
    cleanParameters,
    method: "DELETE",
    parameters,
    url,
    withCredentials
  });
}

export function request<T>({
  body,
  cleanParameters = true,
  method,
  parameters = {},
  url,
  useApiKeys = true,
  withCredentials = false
}: RequestOptions): Promise<T | void> {
  const params = encodeParams(parameters, cleanParameters);
  const apiKeys = useApiKeys ? __API_KEYS : "";
  const urlParams = [apiKeys, params].filter(e => e).join("&");
  const urlWithParams = [url, urlParams].filter(e => e).join("?");

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (xhr.responseText) {
          resolve(JSON.parse(xhr.responseText));
        } else {
          resolve();
        }
      } else {
        reject(new Error("API responds with " + xhr.status));
      }
    };

    xhr.ontimeout = () => {
      reject(new Error(`API request timed out for url ${url}`));
    };

    xhr.onerror = err => {
      reject(err);
    };

    xhr.open(method, urlWithParams, true);
    xhr.withCredentials = withCredentials;
    if (body != null) {
      xhr.setRequestHeader("Content-Type", "application/json");
      xhr.send(JSON.stringify(body));
    } else {
      xhr.send();
    }
  });
}
